import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, BookOpen, Clock, GraduationCap, Filter } from 'lucide-react';
import { getAllAvailableCourses, CourseOption } from '@/hooks/auth/enhancedCourseService';
import { toast } from 'sonner';

interface CoursePreSelectionStepProps {
  onNext: () => void;
  onBack: () => void;
  selectedCourses: string[];
  onCourseToggle: (courseId: string) => void;
  allowSkip?: boolean;
}

const CoursePreSelectionStep: React.FC<CoursePreSelectionStepProps> = ({
  onNext,
  onBack,
  selectedCourses,
  onCourseToggle,
  allowSkip = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [availableCourses, setAvailableCourses] = useState<CourseOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Load courses on component mount
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        const courses = await getAllAvailableCourses();
        setAvailableCourses(courses);
      } catch (error) {
        console.error('Failed to load courses:', error);
        toast.error('Failed to load available courses');
      } finally {
        setLoading(false);
      }
    };
    
    loadCourses();
  }, []);

  const filteredCourses = availableCourses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || course.type === selectedType;
    const matchesLevel = selectedLevel === 'all' || course.level === selectedLevel;
    
    return matchesSearch && matchesType && matchesLevel;
  });

  const courseTypes = Array.from(new Set(availableCourses.map(course => course.type)));
  const courseLevels = Array.from(new Set(availableCourses.map(course => course.level)));

  const getCourseTypeName = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/-/g, ' ');
  };

  if (loading) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Loading Courses...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-6 w-6" />
          Pre-select Courses (Optional)
        </CardTitle>
        <CardDescription>
          Choose courses you'd like to assign to this student. You can also assign courses later from the admin panel.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Course Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {courseTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {getCourseTypeName(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedLevel} onValueChange={setSelectedLevel}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              {courseLevels.map(level => (
                <SelectItem key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Course Selection */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {filteredCourses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No courses found matching your criteria</p>
            </div>
          ) : (
            filteredCourses.map(course => {
              const isSelected = selectedCourses.includes(course.id);
              const foundCourse = availableCourses.find(c => c.id === course.id);
              
              return (
                <div
                  key={course.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => onCourseToggle(course.id)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onCourseToggle(course.id)}
                      className="mt-1"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium">{course.title}</h4>
                        <Badge variant="outline" className="text-xs">
                          {getCourseTypeName(course.type)}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {course.level}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                        {course.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <GraduationCap className="h-3 w-3" />
                          <span>{course.level}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Selection Summary */}
        {selectedCourses.length > 0 && (
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium mb-2">Selected Courses ({selectedCourses.length})</h4>
            <div className="flex flex-wrap gap-2">
              {selectedCourses.map(courseId => {
                const course = availableCourses.find(c => c.id === courseId);
                return course ? (
                  <Badge key={courseId} variant="default" className="text-xs">
                    {course.title}
                  </Badge>
                ) : null;
              })}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          
          <div className="flex gap-2">
            {allowSkip && (
              <Button variant="ghost" onClick={onNext}>
                Skip for Now
              </Button>
            )}
            <Button onClick={onNext}>
              Continue ({selectedCourses.length} selected)
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CoursePreSelectionStep;